import { formatDisplayDate } from 'src/v2/shared/domain/formatDisplayDate';
import type {
  OpenTaskRef,
  RecurringFailureItem,
} from 'src/types/starlight/AuditInsights';
import type { AuditInsightsMessageKey } from '../translations';

/** Separator between meta facts, matching the prototype's dot-delimited line. */
export const SEPARATOR = ' · ';

/**
 * The generator saturates a fail streak at 4, because Helios returns at most three earlier
 * responses per question. A 4 therefore means "at least 4" and the real run may be longer.
 */
export const FAIL_STREAK_CAP = 4;

/**
 * How each streak length reads.
 *
 * A count of 1 does not mean the question failed only once. The generator counts this
 * audit's failure plus the unbroken run before it, and only lists a question at all once an
 * earlier audit failed it, so 1 means the audit immediately before this one did not fail —
 * the failure repeated, but not back-to-back.
 *
 * Discrete keys rather than a templated ordinal: the range is bounded at 4, and an
 * interpolated "{count}th" does not translate.
 */
const STREAK_KEYS = new Map<number, AuditInsightsMessageKey>([
  [1, 'recurringFailNotConsecutive'],
  [2, 'recurringFailSecond'],
  [3, 'recurringFailThird'],
]);

/**
 * How the detail view names a streak: an ordinal, with the reading in brackets.
 *
 * An unexpected count falls back to the mildest phrasing, so a bad number under-claims
 * rather than calling a one-off failure persistent.
 */
export function streakKey(consecutiveFailCount: number): AuditInsightsMessageKey {
  if (consecutiveFailCount >= FAIL_STREAK_CAP) {
    return 'recurringFailPersistent';
  }
  return STREAK_KEYS.get(consecutiveFailCount) ?? 'recurringFailNotConsecutive';
}

/**
 * How a row's summary line states a streak: how many audits it has run for.
 *
 * Deliberately not the same phrasing as {@link streakKey}. A row is scanned against its
 * neighbours, where a count compares directly; the detail view is read on its own, where
 * the ordinal and its reading say more. Both are driven by the same count, so they cannot
 * disagree about the facts.
 *
 * Only a count of two or more can take the counted form — "failed 1 consecutive audits" is
 * neither true nor grammatical.
 */
export function rowStreakKey(consecutiveFailCount: number): AuditInsightsMessageKey {
  if (consecutiveFailCount >= FAIL_STREAK_CAP) {
    return 'recurringRowStreakCapped';
  }
  return consecutiveFailCount > 1 ? 'recurringRowStreak' : 'recurringFailNotConsecutive';
}

/** The one message lookup a meta builder needs, so the builders stay pure. */
export interface MetaFormatters {
  f: (key: AuditInsightsMessageKey, args: Record<string, unknown>) => string;
}

/** Joins the facts that are present. Absent facts leave no empty separator behind. */
export function joinMeta(parts: (string | undefined)[]): string {
  return parts.filter((part): part is string => !!part).join(SEPARATOR);
}

/**
 * One line summarising a repeat failure: how long the streak is, and when the question was
 * last answered.
 *
 * @param item - The recurring failure, whose `priorResults` are newest first.
 * @param formatters - Message lookup for the streak and last-answered phrasing.
 */
export function recurringMeta(item: RecurringFailureItem, { f }: MetaFormatters): string {
  return joinMeta([
    f(rowStreakKey(item.consecutiveFailCount), { count: item.consecutiveFailCount }),
    lastAnswered(item, f),
  ]);
}

/** The newest prior answer, when there is one recorded. */
function lastAnswered(
  item: RecurringFailureItem,
  f: MetaFormatters['f'],
): string | undefined {
  const newest = item.priorResults?.[0];
  if (!newest?.respondedAt) {
    return undefined;
  }

  const date = formatDisplayDate(newest.respondedAt);
  return newest.respondedBy
    ? f('recurringLastAnswered', { date, who: newest.respondedBy })
    : f('recurringLastAnsweredOnly', { date });
}

/**
 * One line summarising an outstanding task: the question it came from, when it was raised,
 * who owns it and when it is due.
 *
 * Every fact past the question id is optional on the model, so each is dropped rather than
 * rendered as a blank.
 */
export function openTaskMeta(task: OpenTaskRef, { f }: MetaFormatters): string {
  return joinMeta([
    // Labelled, so the same question reads the same here as on a failure card.
    f('questionIdChip', { id: task.linkedQuestionId }),
    created(task, f),
    task.assigneeLogin ? f('openTaskAssignee', { who: task.assigneeLogin }) : undefined,
    task.dueDate
      ? f('openTaskDue', { date: formatDisplayDate(task.dueDate) })
      : undefined,
  ]);
}

function created(task: OpenTaskRef, f: MetaFormatters['f']): string | undefined {
  if (!task.createdAt) {
    return undefined;
  }

  const date = formatDisplayDate(task.createdAt);
  return task.createdBy
    ? f('openTaskCreated', { date, who: task.createdBy })
    : f('openTaskCreatedOnly', { date });
}
